import '@availity/yup';
import * as yup from 'yup';

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;
const MM_DD = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const YYYY_MM_DD = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * One call center day. The hours are required only when the day is open: a
 * closed day keeps its values, and demanding them there would stop a company
 * that does not work on Sundays from saving at all.
 */
const weekday = yup
  .object({
    open: yup.boolean(),
    start: yup.string().when('open', {
      is: true,
      then: () =>
        yup.string().matches(HH_MM, 'Formato orario HH:MM').required(),
    }),
    end: yup.string().when('open', {
      is: true,
      then: () =>
        yup.string().matches(HH_MM, 'Formato orario HH:MM').required(),
    }),
  })
  // The rule sits on the object and not on `end` because it crosses two
  // fields — and for a less obvious reason: `when` REPLACES the schema with
  // the `then` branch, so a `.test()` chained after a `.when()` is silently
  // discarded. It used to be written that way, and everything passed.
  .test(
    'chiusura-dopo-apertura',
    "La chiusura deve venire dopo l'apertura",
    (valore) => {
      if (!valore || !valore.open || !valore.start || !valore.end) return true;
      // A string comparison: 'HH:MM' with the leading zero already sorts the
      // way times sort.
      return valore.end > valore.start;
    },
  );

// February gets 29: a recurring closure carries no year, and the 29th is a
// real day every four of them.
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** '02-31' has the shape of a date with no day behind it. */
const isRealRecurringDay = (line) => {
  const [month, day] = line.split('-').map(Number);
  return day <= DAYS_IN_MONTH[month - 1];
};

/**
 * Same for a dated closure, this time with the year to settle February:
 * Date.UTC does not refuse the 29th of a common year, it quietly moves it to
 * March, so the only way to catch it is to read the day back.
 */
const isRealDay = (line) => {
  const [year, month, day] = line.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

/**
 * One date per line, in the given format and on the calendar, ignoring blank
 * lines. The shape alone is not enough: a closure on a day that never comes
 * is a day the call center believes it is closed and never is.
 */
const linesMatching = (regexp, isReal, message) =>
  yup.string().test('lines', message, (value) =>
    String(value || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .every((line) => regexp.test(line) && isReal(line)),
  );

const formValidation = yup.object().shape({
  name: yup.string().required(),
  waitingTimeRecall: yup.string().required(),
  phoneExchange: yup.string().required(),
  timezone: yup.string().required(),
  facebookModule: yup.boolean().required(),
  facebookAdAccountIds: yup.array().of(
    yup.object().shape({
      accountId: yup.string().required(),
      datasetId: yup.string(),
    }),
  ),
  facebookAccessToken: yup.string().when('facebookModule', {
    is: true,
    then: () => yup.string().required(),
  }),
  googleModule: yup.boolean().required(),
  tiktokModule: yup.boolean().required(),
  // accountId is TikTok's advertiser_id. Same shape as facebookAdAccountIds:
  // the functions read [{ accountId }] (insights/tiktok/adAccountIds.js).
  tiktokAdAccountIds: yup.array().of(
    yup.object().shape({
      accountId: yup.string().trim().required(),
    }),
  ),
  // `trim`: spaces would pass `required` and reach TikTok as a token, failing
  // only at the next sync with an authentication error.
  tiktokAccessToken: yup.string().when('tiktokModule', {
    is: true,
    then: () => yup.string().trim().required(),
  }),
  supplierModule: yup.boolean().required(),
  audienceModule: yup.boolean().required(),
  demModule: yup.boolean().required(),
  rcsModule: yup.boolean().required(),
  aiModule: yup.boolean().required(),
  // The Zernio account is the sending WhatsApp number, one per company: the
  // engine reads it off the company at routing time and refuses to start a
  // conversation without it, so with the module on this is as mandatory as
  // the agent itself. `trim` and not plain `required`: spaces would pass
  // required and reach Zernio as an accountId, coming back a 404 only at the
  // first opening attempt. Same shape as facebookAccessToken above.
  zernioAccountId: yup.string().when('aiModule', {
    is: true,
    then: () => yup.string().trim().required(),
  }),
  callCenter: yup.object({
    workingTime: yup.object({
      1: weekday,
      2: weekday,
      3: weekday,
      4: weekday,
      5: weekday,
      6: weekday,
      7: weekday,
    }),
    recurringClosedDays: linesMatching(
      MM_DD,
      isRealRecurringDay,
      'Una data MM-GG per riga, che esista sul calendario',
    ),
    closedDays: linesMatching(
      YYYY_MM_DD,
      isRealDay,
      'Una data AAAA-MM-GG per riga, che esista sul calendario',
    ),
  }),
});

export default formValidation;
